import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  bookingId: string;
  destinationLat?: number | null;
  destinationLng?: number | null;
}

const EtaShareButton = ({ bookingId, destinationLat, destinationLng }: Props) => {
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const share = async () => {
    if (!navigator.geolocation) {
      toast({ title: "Location not supported", variant: "destructive" });
      return;
    }
    setSending(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        let etaMinutes: number | null = null;
        if (destinationLat != null && destinationLng != null) {
          // Rough ETA: haversine distance / 30 km/h average city speed
          const R = 6371;
          const dLat = (destinationLat - latitude) * Math.PI / 180;
          const dLng = (destinationLng - longitude) * Math.PI / 180;
          const a = Math.sin(dLat/2) ** 2 + Math.cos(latitude * Math.PI/180) * Math.cos(destinationLat * Math.PI/180) * Math.sin(dLng/2) ** 2;
          const dist = 2 * R * Math.asin(Math.sqrt(a));
          etaMinutes = Math.max(1, Math.round((dist / 30) * 60));
        }
        const { error } = await supabase.from("booking_eta_updates").insert({
          booking_id: bookingId,
          latitude,
          longitude,
          eta_minutes: etaMinutes,
        });
        setSending(false);
        if (error) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        } else {
          toast({ title: "ETA shared", description: etaMinutes ? `~${etaMinutes} min away` : "Location sent to owner" });
        }
      },
      (err) => {
        setSending(false);
        toast({ title: "Location error", description: err.message, variant: "destructive" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); share(); }} disabled={sending}>
      {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
      Share ETA
    </Button>
  );
};

export default EtaShareButton;
